import { useMemo, useState } from 'react';

// Generic client-side sort for an array of plain objects/rows. `getValue`
// lets callers sort by a derived value (e.g. a formatted date string vs the
// real Date) instead of just `row[key]`.
const useSortableData = (items, initialConfig = null, getValue = (row, key) => row[key]) => {
  const [sortConfig, setSortConfig] = useState(initialConfig);

  const sortedItems = useMemo(() => {
    if (!Array.isArray(items)) return items;
    if (!sortConfig || !sortConfig.key) return items;

    const { key, direction } = sortConfig;
    const sorted = [...items].sort((a, b) => {
      const aVal = getValue(a, key);
      const bVal = getValue(b, key);

      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;

      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return aVal - bVal;
      }

      return String(aVal).localeCompare(String(bVal), undefined, { numeric: true, sensitivity: 'base' });
    });

    return direction === 'desc' ? sorted.reverse() : sorted;
  }, [items, sortConfig, getValue]);

  const requestSort = (key) => {
    setSortConfig(prev => {
      if (!prev || prev.key !== key) return { key, direction: 'asc' };
      if (prev.direction === 'asc') return { key, direction: 'desc' };
      return null; // third click clears sorting for this column
    });
  };

  return { sortedItems, sortConfig, requestSort };
};

export default useSortableData;
